import React, { useMemo } from 'react';
import { colors } from '../../../../../colors';
import { Exchange } from '../../../../../types/exchange';

const ImportantText = ({ text }: { text: string }) => {
  return (
    <span style={{ color: colors.gold, fontWeight: 'bolder' }}>{text}</span>
  );
};

const binanceInstructions = [
  <span key={0}>Log into Binance.</span>,
  <span key={1}>
    Navigate to <ImportantText text={'Account'} /> from the top right menu.
  </span>,
  <span key={2}>
    In the left menu, select <ImportantText text={'API Management'} />.
  </span>,
  <span key={3}>
    Click <ImportantText text={'Create API'} /> on the right and continue with
    system generated.
  </span>,
  <span key={4}>
    You can choose any name for this key, but for example purposes, we can call
    it <ImportantText text={'Automagically'} />.
  </span>,
  <span key={5}>If prompted, verify with authenticator app to continue.</span>,
  <span key={6}>
    Click on <ImportantText text={'Edit Restrictions'} />.
  </span>,
  <span key={7}>
    Make sure <ImportantText text={'Enable Reading'} /> and{' '}
    <ImportantText text={'Enable Spot & Margin Trading'} /> are the only two
    options that are selected.
  </span>,
  <span key={8}>
    Next, select <ImportantText text={'Restrict access to trusted IPs only'} />{' '}
    and input the following ip address: <ImportantText text={'161.97.64.122'} />
    , and click <ImportantText text={'Confirm'} />.
  </span>,
  <span key={9}>
    Press <ImportantText text={'Save'} /> and verify once again.
  </span>,
  <span key={10}>
    Now you are ready to copy both the <ImportantText text={'key'} /> and the{' '}
    <ImportantText text={'secret'} /> to form on the left.
  </span>,
  <span key={11}>
    Caution, once you leave the api management page on Binance, you will no
    longer be able to see the secret.
  </span>,
];

const kucoinInstructions = [
  <span key={0}>Log into KuCoin.</span>,
  <span key={1}>
    Click your profile icon in the top right and navigate to{' '}
    <ImportantText text={'API Management'} />.
  </span>,
  <span key={2}>
    Click <ImportantText text={'Create API'} />.
  </span>,
  <span key={3}>
    You can choose any name for this key, but for example purposes, we can call
    it <ImportantText text={'Automagically'} />.
  </span>,
  <span key={4}>
    Enter an <ImportantText text={'API Passphrase'} /> to your liking, you will
    need to enter that in the form to the left.
  </span>,
  <span key={5}>
    At <ImportantText text={'API Restrictions'} />, Only select{' '}
    <ImportantText text={'Spot Trading'} />.
  </span>,
  <span key={6}>
    Next, select <ImportantText text={'Restricted to Trusted IPs Only'} /> and
    input the following ip address: <ImportantText text={'161.97.64.122'} />
    , and click <ImportantText text={'Add'} />.
  </span>,
  <span key={7}>
    Press <ImportantText text={'Next'} /> and verify your identification.
  </span>,
  <span key={8}>
    Now you are ready to copy both the <ImportantText text={'key'} />,{' '}
    <ImportantText text={'secret'} /> and the{' '}
    <ImportantText text={'passphrase'} /> to form on the left.
  </span>,
  <span key={9}>
    Caution, once you leave the api management page on KuCoin, you will no
    longer be able to see the secret.
  </span>,
  <span key={10}>
    Caution, your balance on KuCoin should be inside your{' '}
    <ImportantText text={'Trading Account'} /> to be accessable by the bot.
  </span>,
];

const mexcInstructions = [
  <span key={0}>Log into MexC.</span>,
  <span key={1}>
    Click your profile icon in the top right and navigate to{' '}
    <ImportantText text={'API Management'} />.
  </span>,
  <span key={2}>
    For <ImportantText text={'SPOT'} />, you should select{' '}
    <ImportantText text={'View Account Details'} />,{' '}
    <ImportantText text={'View Order Details'} /> and{' '}
    <ImportantText text={'Trade'} />.
  </span>,
  <span key={3}>
    For <ImportantText text={'Notes'} />, You can choose any name for this key,
    but for example purposes, we can call it{' '}
    <ImportantText text={'Automagically'} />.
  </span>,
  <span key={4}>
    For <ImportantText text={'Link IP Address'} />, input the following ip
    address: <ImportantText text={'161.97.64.122'} />.
  </span>,
  <span key={5}>
    Read and confirm{' '}
    <ImportantText
      text={'I have read and agree to the Risk Reminders for API Users'}
    />
    .
  </span>,
  <span key={6}>
    Press <ImportantText text={'Create'} /> and verify your identification.
  </span>,
  <span key={7}>
    Now you are ready to copy both the <ImportantText text={'key'} /> and the{' '}
    <ImportantText text={'secret'} /> to form on the left.
  </span>,
  <span key={8}>
    Caution, once you leave the api management page on MexC, you will no longer
    be able to see the secret.
  </span>,
];

const coinbaseInstructions: Array<string> = [];

const Instructions = ({ exchange }: { exchange: Exchange | undefined }) => {
  const title = useMemo(() => {
    switch (exchange) {
      case Exchange.Binance:
        return 'Binance Instructions:';

      case Exchange.KuCoin:
        return 'KuCoin Instructions:';

      case Exchange.MexC:
        return 'Mexc Instructions:';

      case Exchange.Coinbase:
        return 'Coinbase Instructions:';
    }
  }, [exchange]);

  const instructions = useMemo(() => {
    switch (exchange) {
      case Exchange.Binance:
        return binanceInstructions;

      case Exchange.KuCoin:
        return kucoinInstructions;

      case Exchange.MexC:
        return mexcInstructions;

      case Exchange.Coinbase:
        return coinbaseInstructions;
    }

    return [];
  }, [exchange]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 40,
        right: 40,
        maxWidth: '35vw',
        maxHeight: '40vh',
        border: '2px solid gray',
        overflowY: 'auto',
        zIndex: 500,
        borderRadius: 8,
        padding: 16,
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          fontWeight: 'bold',
          fontSize: 16,
          color: colors.white,
          margin: 0,
          marginBottom: 4,
        }}
      >
        {title}
      </p>
      {instructions.map((instruction, index) => (
        <p
          style={{
            color: colors.white,
            fontSize: 14,
            margin: 0,
            marginTop: 6,
            marginBottom: 6,
          }}
          key={index}
        >
          <span
            style={{
              fontSize: 18,
              fontWeight: 'bold',
              color: colors.gold,
              textTransform: 'uppercase',
              marginRight: 4,
            }}
          >
            {index + 1}.
          </span>{' '}
          {instruction}
        </p>
      ))}
    </div>
  );
};

export default Instructions;
